import { FormBlock } from '@qbe/ads-blocks-forms';
import type { CheckboxProps } from '@qbe/ads-core';
import {
  Box,
  Button,
  Checkbox,
  FormGroupLabel,
  Spacer,
  TextField,
  Typography,
} from '@qbe/ads-core';
import React from 'react';
import { Controller, useController, useFormContext } from 'react-hook-form';

import { useSnackbar } from '~/components-ds/Snackbar';
import { useAppContent, useAppDispatch } from '~/hooks';
import { useAbn } from '~/hooks/useAbn';
import { qalTrackEventAction } from '~/thunks/analytics';
import { TextFieldState } from '~/types/enums';
import { validateABN } from '~/utils/abnValidator';
import { analyticsConfig } from '~/utils/analytics/analyticsConfig';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

interface ABNProps {
  onNoAbn?: (noAbn: boolean) => void;
}

interface FormFields {
  abn: string;
  noAbn: boolean;
}

export const formatSpaces = (value: string) => value.replace(/\s/g, '');

const ABN = ({ onNoAbn }: ABNProps) => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.extraInfoMoreBusinessDetails',
  });
  const { trigger, getValues, control, register, unregister, setValue, watch } =
    useFormContext<FormFields>();

  const { fieldState: abnFieldState } = useController({
    control,
    name: 'abn',
  });
  const { noAbn, setAbn, setNoAbn, businessNameLookup, triggerAbnLookup } = useAbn();
  const { enqueueSnackbar } = useSnackbar();
  const noAbnChecked = watch('noAbn');
  const dispatch = useAppDispatch();

  const handleChangeAbnStatus: CheckboxProps['onChange'] = (event, checked) => {
    setValue('noAbn', checked);
    setNoAbn(checked);
    onNoAbn?.(checked);
    if (checked) {
      register('abn');
    } else {
      unregister('abn');
    }
    // trigger async
    trigger('noAbn');
  };

  const doTriggerAbnLookup = () => {
    const trackAbnEvent = (abnApiPayload: string) => {
      dispatch(
        qalTrackEventAction({
          event: 'abn_lookup',
          eventAction: abnApiPayload,
          eventLabel: abnApiPayload,
        })
      );
    };
    // Analytics - tracking abn look up start.
    trackAbnEvent('abn lookup start');
    const enqueueErrorSnackEvent = () => {
      enqueueSnackbar(getTextContent('fields.abnLookupSnackbar.errorText'), {
        variant: 'error',
        heading: getTextContent('fields.abnLookupSnackbar.title'),
      });
    };
    const abn = getValues('abn');
    if (abn) {
      triggerAbnLookup(abn)
        .then((result) => {
          if (result.isSuccess && result.data !== null) {
            // Analytics - trigger abn lookup success and set the analytics private variable.
            trackAbnEvent('abn lookup success');
            analyticsConfig.abnLookUpStatus = true;
            enqueueSnackbar(getTextContent('fields.abnLookupSnackbar.successText'), {
              variant: 'success',
              heading: getTextContent('fields.abnLookupSnackbar.title'),
            });
          } else if (result.isSuccess && result.data === null) {
            // Analytics - tracking abn lookup success, but no data return.
            trackAbnEvent('abn lookup no data');
            enqueueSnackbar(getTextContent('fields.abnLookupSnackbar.warningText'), {
              variant: 'warning',
              heading: getTextContent('fields.abnLookupSnackbar.title'),
            });
          } else {
            // Analytics - abn lookup failed.
            trackAbnEvent('abn lookup failed');
            enqueueErrorSnackEvent();
          }
        })
        .catch(() => {
          // Analytics - abn lookup api error.
          trackAbnEvent('abn lookup api error');
          enqueueErrorSnackEvent();
        });
    }
  };

  let helperText = getTextContent('fields.abn.helperText');
  let errorText: string = getTextContent('fields.abn.invalidText');
  let textFieldStateWithLookup: TextFieldState | undefined;
  const isAbnMatchFound = businessNameLookup.isSuccess && businessNameLookup.value !== null;

  if (businessNameLookup.isFetching) {
    helperText = getTextContent('fields.abn.helperTextLooking');
  } else if (isAbnMatchFound) {
    helperText = getTextContent('fields.abn.helperTextMatchFound');
    textFieldStateWithLookup = TextFieldState.Success;
  } else if (businessNameLookup.isSuccess || businessNameLookup.value === null) {
    errorText = getTextContent('fields.abn.invalidTextMatchNotFound');
    helperText = '';
    textFieldStateWithLookup = TextFieldState.Error;
  } else if (businessNameLookup.isError) {
    errorText = getTextContent('fields.abn.invalidTextUnableToLook');
    helperText = '';
    textFieldStateWithLookup = TextFieldState.Error;
  }

  const contextualHelpDescription = getJSXContent('contextualhelpers.abn.description');
  return (
    <>
      <FormGroupLabel
        title={getTextContent('fields.abn.title')}
        alignCenter
        subtitle={getTextContent('fields.abn.description')}
        titleSuffix={
          <ContextualHelpWithButton tracking="help: What's your ABN?">
            <Typography>{contextualHelpDescription}</Typography>
          </ContextualHelpWithButton>
        }
      />

      <Controller
        control={control}
        shouldUnregister={noAbn}
        name="abn"
        render={({ field: { ref, ...field }, fieldState }) => {
          const hasError = Boolean(
            textFieldStateWithLookup === TextFieldState.Error ||
            !!(!noAbnChecked && fieldState.error)
          );

          return (
            <TextField
              {...field}
              id="abn"
              type="tel"
              label={getTextContent('fields.abn.label')}
              inputRef={ref}
              onChange={(e) => {
                const value = formatSpaces(e.target.value);
                field.onChange(value);
                setAbn(value);
              }}
              error={hasError}
              displayIcon
              success={isAbnMatchFound}
              helperText={hasError ? errorText : helperText}
              data-track-value="mask"
              disabled={!!noAbn || businessNameLookup.isFetching}
            />
          );
        }}
        rules={{
          validate: noAbnChecked ? undefined : validateABN,
          required: !noAbnChecked,
        }}
      />
      <Spacer direction="column" size="xs" />

      <Box width="100%" maxWidth="400px">
        <Controller
          name="noAbn"
          render={({ field: { ref: _, ...field } }) => (
            <Checkbox
              {...field}
              id="no-abn"
              checked={noAbn!}
              disabled={!!watch('abn')}
              onChange={handleChangeAbnStatus}
              data-track-checkbox="I don't have an ABN - checkbox"
              data-track="I don't have an ABN"
              label={getTextContent('fields.noAbn.title')}
            />
          )}
        />
      </Box>

      {!noAbn && (
        <FormBlock preset="button" paddingBottom="md">
          <Button
            label={getTextContent('buttons.prefillBusinessName')}
            data-track="prefill business name"
            onClick={doTriggerAbnLookup}
            disabled={!!abnFieldState.error || businessNameLookup.isFetching || !watch('abn')}
          />
        </FormBlock>
      )}
    </>
  );
};

export default ABN;
